import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Divider,
  Link
} from '@mui/material';
import { Business, LockOutlined } from '@mui/icons-material';
import { simpleAuth } from '../services/simpleAuth';

const SimpleAuth = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    company: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        // Simple Login
        const result = await simpleAuth.signIn(form.email, form.password);
        
        if (!result.success) {
          setError(result.error || 'Login failed');
          return;
        }

        if (result.user) {
          // CRITICAL FIX: Ensure company name is saved
          if (result.user.company) {
            simpleAuth.updateCompanyName(result.user.company);
          }
          
          localStorage.setItem('clientToken', result.token);
          localStorage.setItem('clientData', JSON.stringify(result.user));
          onLogin(result.user, result.token);
        }
      } else {
        // Simple Registration
        if (!form.name || !form.company) {
          setError('Please fill in all fields');
          return;
        }

        const userData = {
          fullName: form.name,
          company_name: form.company,
          company: form.company,
          subscription_tier: 'free'
        };

        const result = await simpleAuth.signUp(form.email, form.password, userData);
        
        if (!result.success) {
          setError(result.error || 'Registration failed');
          return;
        }

        if (result.user) {
          // CRITICAL FIX: Ensure company name is saved immediately
          simpleAuth.updateCompanyName(form.company);
          
          localStorage.setItem('clientToken', result.token);
          localStorage.setItem('clientData', JSON.stringify(result.user));
          localStorage.setItem('companyName', form.company);
          localStorage.setItem('userCompany', form.company);
          onLogin(result.user, result.token);
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <Box sx={{ backgroundColor: 'primary.main', borderRadius: '50%', p: 1, mb: 2 }}>
            {isLogin ? <LockOutlined sx={{ color: 'white', fontSize: 40 }} /> : <Business sx={{ color: 'white', fontSize: 40 }} />}
          </Box>
          
          <Typography component="h1" variant="h4" gutterBottom>
            {isLogin ? 'Sign In' : 'Create Account'}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            {isLogin ? 'Welcome back to your social media dashboard' : 'Join thousands of businesses managing their social media presence'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {!isLogin && (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  autoFocus
                />
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Company Name"
                  name="company"
                  value={form.company}
                  onChange={handleChange}
                />
              </>
            )}
            
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              autoFocus={isLogin}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              value={form.password}
              onChange={handleChange}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </Box>

          <Divider sx={{ width: '100%', my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
            </Typography>
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setForm({ email: '', password: '', name: '', company: '' });
              }}
              sx={{ textTransform: 'none', fontSize: '0.875rem' }}
            >
              {isLogin ? 'Create Account' : 'Sign In'}
            </Button>
          </Box>

          {isLogin && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Your Credentials:
              </Typography>
              <Typography variant="body2" color="primary" sx={{ fontFamily: 'monospace' }}>
                demo@example.com / demo123
              </Typography>
              <Typography variant="body2" color="primary" sx={{ fontFamily: 'monospace' }}>
                admin@example.com / admin123
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default SimpleAuth;
